import React from "react";
import { CalendarDays, ChevronDown } from "lucide-react";
import { CardShell, FieldLabel } from "./card-handler";


interface RunAtSpecificTimeConfig {
  fixedTime?: string;
  triggerInterval?: string;
  timezone?: string;
  recipients?: string;
}

const INTERVAL_OPTIONS = [
  { label: "Daily", value: "Days" },
  { label: "Weekly", value: "Weeks" },
  { label: "Monthly", value: "Months" },
  { label: "Once", value: "Once" },
];

interface RunAtSpecificTimeActionCardProps {
  config: RunAtSpecificTimeConfig;
  onUpdate: (config: RunAtSpecificTimeConfig) => void;
  onDelete: () => void;
  onReset: () => void;
  bare?: boolean;
}

export const RunAtSpecificTimeActionCard: React.FC<RunAtSpecificTimeActionCardProps> = ({
  config,
  onUpdate,
  onDelete,
  onReset,
  bare,
}) => {
  const update = (patch: Partial<RunAtSpecificTimeConfig>) =>
    onUpdate({ ...config, ...patch });

  return (
    <CardShell
      title="Run at Specific Time"
      description="Fires once at a specific date and time. No inbound message is required."
      onDelete={onDelete}
      onReset={onReset}
      bare={bare}
    >
        <FieldLabel>Date and Time</FieldLabel>
        <div className="relative mb-3">
          <input
            type="datetime-local"
            value={config.fixedTime ?? ""}
            onChange={(e) => update({ fixedTime: e.target.value })}
            className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-indigo-300 pr-9"
          />
          <CalendarDays size={17} className="absolute right-3 top-2.5 text-gray-500 dark:text-gray-400 pointer-events-none" />
        </div>

        <FieldLabel>Repeat Every</FieldLabel>
        <div className="relative mb-3">
          <select
            value={config.triggerInterval ?? "Days"}
            onChange={(e) => update({ triggerInterval: e.target.value })}
            className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 focus:outline-none focus:ring-1 focus:ring-indigo-300"
          >
            {INTERVAL_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="absolute right-2 top-2.5 pointer-events-none">
            <ChevronDown size={16} className="text-gray-500 dark:text-gray-400" />
          </div>
        </div>

        <FieldLabel>Recipients</FieldLabel>
        <textarea
          value={config.recipients ?? ""}
          onChange={(e) => update({ recipients: e.target.value })}
          rows={4}
          placeholder="Enter phone numbers (one number per line)"
          className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-300 placeholder-gray-400 outline-none focus:ring-1 focus:ring-indigo-300 resize-none"
        />
    </CardShell>
  );
};